import type { EntityItemConfig, EntitySelection } from "@salestrak/ui";
import type { EntityRefValue } from "#app/lib/data";
import {
  entityFromAssigneeRef,
  iconSourceToName,
  resolveEntityLabel,
  type EntityData,
} from "#app/lib/entity-visuals";
import { isInvited, isSuspended } from "#app/lib/role-utils";
import { EntityTag } from "#app/components/entity";

export function normalizeValue(value: string | string[] | null | undefined): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export function selectionToUserEntity(sel: EntitySelection): EntityData {
  const e = sel.entity;
  return {
    kind: "user",
    id: sel.id,
    user: {
      name: e.title,
      profileImage: e.avatar?.src ?? null,
      email: (e.data?.email as string | null | undefined) ?? null,
      memberStatus: (e.data?.memberStatus as string | null | undefined) ?? null,
    },
  };
}

// Teams key their label as `teamName`; workflow/issueType use `name`.
function selectionToIconEntity<K extends "team" | "workflow" | "issueType">(
  sel: EntitySelection,
  kind: K,
): EntityData {
  const e = sel.entity;
  const icon = {
    icon: iconSourceToName(e.avatar?.icon),
    color: e.avatar?.color ?? null,
  };
  if (kind === "team") {
    return { kind: "team", id: sel.id, team: { teamName: e.title, icon } };
  }
  return {
    kind,
    id: sel.id,
    [kind]: { name: e.title, icon },
  } as EntityData;
}

export function selectionToTeamEntity(sel: EntitySelection): EntityData {
  return selectionToIconEntity(sel, "team");
}

export function selectionToWorkflowEntity(sel: EntitySelection): EntityData {
  return selectionToIconEntity(sel, "workflow");
}

export function selectionToIssueTypeEntity(sel: EntitySelection): EntityData {
  return selectionToIconEntity(sel, "issueType");
}

// Issue sources have no per-item glyph; the label renders on its own (no icon
// passed, so EntityVisual renders nothing).
export function selectionToIssueSourceEntity(sel: EntitySelection): EntityData {
  return {
    kind: "issueSource",
    id: sel.id,
    issueSource: {
      name: sel.entity.title,
      icon: {
        icon: iconSourceToName(sel.entity.avatar?.icon),
        color: sel.entity.avatar?.color ?? null,
      },
    },
  };
}

// Issues/projects key their label as `name`; the workflow rides in `data` so the
// resolved entity keeps the stage→workflow link a chip may need.
export function selectionToIssueEntity(sel: EntitySelection): EntityData {
  const e = sel.entity;
  return {
    kind: "issue",
    id: sel.id,
    issue: {
      name: e.title,
      icon: {
        icon: iconSourceToName(e.avatar?.icon),
        color: e.avatar?.color ?? null,
      },
    },
    workflowId: (e.data?.workflowId as string | null | undefined) ?? undefined,
  };
}

export function selectionToStageEntity(
  sel: EntitySelection,
  workflowId?: string,
): EntityData {
  const e = sel.entity;
  const icon = e.data?.icon as
    | { color?: string | null; percentage?: number | null }
    | undefined;
  return {
    kind: "stage",
    id: sel.id,
    stage: {
      name: e.title,
      type: (e.data?.type as string | null | undefined) ?? null,
      icon: icon ?? null,
    },
    workflowId,
  };
}

// Assignees are members-only, so an assignee ref is always a user.
// `render` drives the display; the primitive src stays as data so selections
// round-trip through selectionToUserEntity.
export function assigneeRefToEntityItem(ref: EntityRefValue): EntityItemConfig {
  const entity = entityFromAssigneeRef(ref);
  return {
    id: ref.entityId,
    title: ref.user ? resolveEntityLabel(entity) : ref.entityId,
    description: ref.isPrimary ? "Primary" : undefined,
    muted: isSuspended(ref.user),
    invited: isInvited(ref.user),
    avatar: {
      src: ref.user?.profileImage ?? undefined,
      render: () => (
        <EntityTag entity={entity} display="visual" size="sm" interactive={false} />
      ),
    },
    keywords: ref.user?.email ? [ref.user.email] : undefined,
    data: {
      email: ref.user?.email ?? undefined,
      entityType: ref.entityType,
      isPrimary: ref.isPrimary ?? false,
      memberStatus: ref.user?.memberStatus ?? null,
    },
  };
}
